import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { CheckCircle, AlertCircle, LoaderCircle } from "lucide-react";
import {
  resolveConsumerRuntimeApiBase,
  resolveConsumerRuntimeConnectionUuid,
  resolveConsumerRuntimeEventsBase,
  resolveConsumerRuntimeMarketDomain,
} from "../runtime-endpoints";
import {
  persistPlatformMetadata,
  readPlatformMetadata,
  resolveFreshSetupMarketDomain,
} from "../platform-metadata";
import { trn } from "../shared/i18n";
import {
  readConsumerRuntimeDeviceMode,
  readConsumerRuntimeTableCode,
  writeConsumerRuntimeDeviceMode,
  writeConsumerRuntimeTableCode,
} from "../runtime-device-mode";
import LanguageSelector from "../components/LanguageSelector";
import { useI18n } from "../shared/i18n";
import { refreshBootstrap } from "../runtime-bootstrap";
import { preloadMenuImages } from "../menu-images";

type ActivationStep = "idle" | "connecting" | "downloadingMenus" | "preparingImages" | "saving";

const activationProgress: Record<ActivationStep, number> = {
  idle: 0,
  connecting: 18,
  downloadingMenus: 52,
  preparingImages: 82,
  saving: 96,
};

export default function Setup() {
  const { language } = useI18n();
  // Production has already resolved and integrity-checked the canonical
  // domain snapshot before this component can render. Setup must never start
  // a second, competing tenant/branding resolution.
  const requireBrandBeforeSetup = false;
  const navigate = useNavigate();
  const [terminalIdentifier, setTerminalIdentifier] = useState(resolveConsumerRuntimeConnectionUuid() || "");
  const [deviceMode, setDeviceMode] = useState<"kiosk" | "table">(() => readConsumerRuntimeDeviceMode());
  const [tableCode, setTableCode] = useState(() => readConsumerRuntimeTableCode());
  const [loading, setLoading] = useState(false);
  const [activationStep, setActivationStep] = useState<ActivationStep>("idle");
  const [imageProgress, setImageProgress] = useState({ loaded: 0, total: 0 });
  const [err, setErr] = useState<string | null>(null);
  const [platformMetadata] = useState(() => readPlatformMetadata());
  const hostMetadataChecked = true;
  const hostMetadataMissing = false;
  const [failedLogoUrls, setFailedLogoUrls] = useState<string[]>([]);
  const isDisqoberPlatform = /disqober/i.test(
    `${platformMetadata.platformCode || ""} ${platformMetadata.platformName || ""}`
  );
  const setupLogoCandidates = Array.from(
    new Set(
      [
        platformMetadata.logoLightUrl,
        platformMetadata.iconUrl,
        isDisqoberPlatform ? "/disqober-icon.svg" : "",
      ].filter((value): value is string => Boolean(value))
    )
  );
  const setupLogoUrl = setupLogoCandidates.find((url) => !failedLogoUrls.includes(url)) || "";
  const metadataReady = true;
  const canActivate = Boolean(terminalIdentifier.trim());
  const currentActivationProgress =
    activationStep === "preparingImages" && imageProgress.total > 0
      ? Math.min(95, 82 + Math.round((imageProgress.loaded / imageProgress.total) * 13))
      : activationProgress[activationStep];

  useEffect(() => {
    setFailedLogoUrls([]);
  }, [platformMetadata.logoLightUrl, platformMetadata.iconUrl, platformMetadata.platformCode]);

  const showAxiosError = (error: unknown) => {
    if (error instanceof Error && error.message === "MISSING_PLATFORM_BRAND_METADATA") {
      return trn(
        "app.consumer.setup.unavailable_for_domain",
        "This application is not available for this domain yet."
      );
    }
    const ax = error as AxiosError<any>;
    if (ax.code === "ERR_NETWORK") return trn("app.consumer.setup.network_error", "Could not connect to the local server.");
    const message = ax.response?.data?.errors?.[0]?.message;
    return message || trn("app.consumer.setup.bootstrap_error", "Could not initialize the kiosk.");
  };

  const onActivate = async () => {
    setErr(null);
    const clean = terminalIdentifier.trim();
    if (!clean) {
      setErr(trn("app.consumer.setup.connection_required", "Enter the connection UUID."));
      return;
    }
    if (deviceMode === "table" && !tableCode.trim()) {
      setErr(trn("app.consumer.setup.table_required", "Enter the table code for table mode."));
      return;
    }

    setLoading(true);
    setActivationStep("connecting");
    setImageProgress({ loaded: 0, total: 0 });
    try {
      if (requireBrandBeforeSetup && hostMetadataMissing) {
        throw new Error("MISSING_PLATFORM_BRAND_METADATA");
      }
      if (requireBrandBeforeSetup && !metadataReady) {
        throw new Error("MISSING_PLATFORM_BRAND_METADATA");
      }
      const apiBaseUrl = resolveConsumerRuntimeApiBase(clean);
      const eventsBaseUrl = resolveConsumerRuntimeEventsBase(clean);
      const confirmedMetadata = platformMetadata;
      const freshMarketDomain = resolveFreshSetupMarketDomain();
      setActivationStep("downloadingMenus");
      const { payload, normalizedMenus } = await refreshBootstrap(clean, {
        language,
        refreshPlatformBranding: true,
        pageTitle: "Consumer",
        preserveStoredLanguage: false,
      });
      setActivationStep("preparingImages");
      await preloadMenuImages(normalizedMenus, {
        onProgress: ({ loaded, total }) => setImageProgress({ loaded, total }),
      });
      setActivationStep("saving");
      persistPlatformMetadata(confirmedMetadata);
      writeConsumerRuntimeDeviceMode(deviceMode);
      writeConsumerRuntimeTableCode(deviceMode === "table" ? tableCode : "");
      localStorage.setItem("connectionUuid", clean);
      localStorage.removeItem("sellingPointId");
      localStorage.removeItem("terminalId");
      localStorage.setItem("consumerRuntimeApiBaseUrl", apiBaseUrl);
      const marketDomain = freshMarketDomain || resolveConsumerRuntimeMarketDomain();
      if (marketDomain) localStorage.setItem("consumerRuntimeMarketDomain", marketDomain);
      else localStorage.removeItem("consumerRuntimeMarketDomain");
      if (eventsBaseUrl) localStorage.setItem("consumerRuntimeEventsBaseUrl", eventsBaseUrl);
      else localStorage.removeItem("consumerRuntimeEventsBaseUrl");
      localStorage.setItem("consumerRuntimeSetupEnvelope", JSON.stringify({
        connectionUuid: clean,
        deviceMode,
        tableCode: deviceMode === "table" ? tableCode.trim() : "",
        apiBaseUrl,
        eventsBaseUrl,
        metadata: confirmedMetadata,
        bootstrap: {
          kioskConfig: payload.kioskConfig || {},
          restaurantBrand: payload.restaurantBrand || null,
          marketplaceBrand: payload.marketplaceBrand || null,
          menusCount: normalizedMenus.length,
        },
        activatedAt: new Date().toISOString(),
      }));
      localStorage.setItem("platformAppName", String(confirmedMetadata.appName || confirmedMetadata.platformName || "Kiosk"));
      localStorage.setItem("platformCode", String(confirmedMetadata.platformCode || ""));
      localStorage.setItem(
        "restaurantName",
        String(
          payload?.restaurantBrand?.restaurantName ||
            payload?.restaurantBrand?.displayName ||
            payload?.restaurantBrand?.name ||
            payload?.restaurantBrand?.metadata?.restaurantName ||
            "Restaurante"
        )
      );
      const logo =
        payload?.restaurantBrand?.assets?.logoUrl ||
        payload?.restaurantBrand?.assets?.logo ||
        payload?.restaurantBrand?.metadata?.logoUrl ||
        payload?.restaurantBrand?.metadata?.logo ||
        confirmedMetadata.logoLightUrl ||
        confirmedMetadata.iconUrl ||
        "";
      if (logo) localStorage.setItem("restaurantLogo", String(logo));
      else localStorage.removeItem("restaurantLogo");
      navigate("/", { replace: true });
    } catch (error) {
      setErr(showAxiosError(error));
      localStorage.removeItem("connectionUuid");
      localStorage.removeItem("terminalId");
      localStorage.removeItem("sellingPointId");
      localStorage.removeItem("marketplaceBackgroundImage");
    } finally {
      setLoading(false);
      setActivationStep("idle");
      setImageProgress({ loaded: 0, total: 0 });
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !loading) void onActivate();
  };

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        minHeight: "100vh",
        background: "#000",
        padding: 24,
      }}
    >
      <div
        className="p-4 p-md-5"
        style={{
          width: "min(520px, 92vw)",
          background: "rgba(15, 23, 42, 0.88)",
          borderRadius: 28,
          color: "#fff",
          border: "1px solid rgba(148, 163, 184, 0.22)",
          boxShadow: "0 28px 90px rgba(0, 0, 0, 0.45)",
        }}
      >
        <div className="mb-4">
          <LanguageSelector dark variant="dropdown" />
        </div>
        {!hostMetadataChecked ? (
          <div className="text-center py-5" style={{ color: "#cbd5e1" }}>
            <LoaderCircle size={34} className="mx-auto mb-3 spinner" />
            {trn("app.consumer.setup.loading_brand", "Loading platform brand...")}
          </div>
        ) : requireBrandBeforeSetup && hostMetadataMissing ? (
          <div className="text-center">
            <div
              style={{
                width: 76,
                height: 76,
                borderRadius: 22,
                margin: "0 auto 18px",
                display: "grid",
                placeItems: "center",
                background: "#1e293b",
                color: "#fff",
                fontWeight: 800,
                fontSize: 28,
              }}
            >
              !
            </div>
            <p className="mb-2 text-uppercase fw-semibold" style={{ color: "#fbbf24", letterSpacing: "0.12em", fontSize: 12 }}>
              {trn("app.consumer.setup.not_available_tag", "Not available")}
            </p>
            <h1 className="mb-3" style={{ fontSize: "2rem", fontWeight: 800 }}>
              {trn("app.consumer.setup.not_ready_title", "This application is not ready yet")}
            </h1>
            <p className="mb-0" style={{ color: "#cbd5e1", lineHeight: 1.6 }}>
              {trn("app.consumer.setup.not_ready_body", "This kiosk cannot be used from this domain yet.")}
            </p>
            <div
              className="mt-4"
              style={{
                borderRadius: 18,
                background: "rgba(15, 23, 42, 0.7)",
                border: "1px solid rgba(248, 113, 113, 0.25)",
                padding: "14px 16px",
                color: "#fca5a5",
                textAlign: "left",
              }}
            >
              <strong>{trn("app.consumer.setup.temporarily_unavailable", "Temporarily unavailable")}</strong>
              <div className="mt-2">
                {trn(
                  "app.consumer.setup.contact_platform_admin",
                  "Contact the platform administrator and try again later."
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
        <div className="text-center mb-4">
          {setupLogoUrl ? (
            <img
              src={setupLogoUrl}
              alt={platformMetadata.platformName}
              onError={() =>
                setFailedLogoUrls((current) =>
                  current.includes(setupLogoUrl) ? current : [...current, setupLogoUrl]
                )
              }
              style={{ maxWidth: 220, maxHeight: 68, objectFit: "contain", marginBottom: 18 }}
            />
          ) : (
            <div
              style={{
                width: 76,
                height: 76,
                borderRadius: 22,
                margin: "0 auto 18px",
                display: "grid",
                placeItems: "center",
                background: platformMetadata.secondaryColor,
                color: "#fff",
                fontWeight: 800,
                fontSize: 28,
              }}
            >
              {String(platformMetadata.platformName || "V").slice(0, 1).toUpperCase()}
            </div>
          )}

          <p className="mb-2 text-uppercase fw-semibold" style={{ color: "#93c5fd", letterSpacing: "0.12em", fontSize: 12 }}>
            {platformMetadata.platformName || "Platform"}
          </p>
          <h1 className="mb-3" style={{ fontSize: "2rem", fontWeight: 800 }}>
            {deviceMode === "table"
              ? trn("app.consumer.setup.activate_table_title", "Activate table device")
              : trn("app.consumer.setup.activate_kiosk_title", "Activate kiosk")}
          </h1>
          <p className="mb-0" style={{ color: "#cbd5e1", lineHeight: 1.6 }}>
            {deviceMode === "table"
              ? trn(
                  "app.consumer.setup.activate_table_body",
                  "Enter the connection UUID and the table code to activate this table device."
                )
              : trn(
                  "app.consumer.setup.activate_kiosk_body",
                  "Enter the connection UUID to activate this kiosk device."
                )}
          </p>
        </div>

        <div className="d-flex gap-3 mt-3 mb-3">
          <button
            type="button"
            className="btn flex-fill"
            disabled={loading}
            onClick={() => setDeviceMode("kiosk")}
            style={{
              borderRadius: 18,
              border: deviceMode === "kiosk" ? "1px solid rgba(96, 165, 250, 0.6)" : "1px solid rgba(148, 163, 184, 0.2)",
              background: deviceMode === "kiosk" ? "rgba(30, 64, 175, 0.35)" : "rgba(15, 23, 42, 0.7)",
              color: "#fff",
              minHeight: 72,
            }}
          >
            <div className="fw-bold">{trn("app.consumer.setup.mode_kiosk", "Kiosk mode")}</div>
            <div style={{ fontSize: 13, color: "#cbd5e1" }}>{trn("app.consumer.setup.mode_kiosk_hint", "Standalone self-order device")}</div>
          </button>
          <button
            type="button"
            className="btn flex-fill"
            disabled={loading}
            onClick={() => setDeviceMode("table")}
            style={{
              borderRadius: 18,
              border: deviceMode === "table" ? "1px solid rgba(96, 165, 250, 0.6)" : "1px solid rgba(148, 163, 184, 0.2)",
              background: deviceMode === "table" ? "rgba(30, 64, 175, 0.35)" : "rgba(15, 23, 42, 0.7)",
              color: "#fff",
              minHeight: 72,
            }}
          >
            <div className="fw-bold">{trn("app.consumer.setup.mode_table", "Table mode")}</div>
            <div style={{ fontSize: 13, color: "#cbd5e1" }}>{trn("app.consumer.setup.mode_table_hint", "Tablet assigned to one restaurant table")}</div>
          </button>
        </div>

        <div className="text-center mb-2" style={{ color: "#93c5fd", fontSize: 13, fontWeight: 700, letterSpacing: "0.06em" }}>
          {trn("app.consumer.setup.connection_uuid_label", "CONNECTION UUID")}
        </div>
        <input
          type="text"
          className="form-control text-center fs-4 p-3"
          placeholder="550e8400-e29b-41d4-a716-446655440000"
          value={terminalIdentifier}
          onChange={(e) => setTerminalIdentifier(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={loading}
          autoFocus
          style={{
            borderRadius: 18,
            border: "1px solid rgba(96, 165, 250, 0.35)",
            background: "rgba(15, 23, 42, 0.92)",
            color: "#fff",
          }}
        />
        <p className="mt-2 mb-0 text-center" style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.5 }}>
          {trn(
            "app.consumer.setup.connection_uuid_hint",
            "Use the connection UUID configured for this kiosk. Legacy terminal and selling point identifiers are still accepted only during migration."
          )}
        </p>

        {deviceMode === "table" ? (
          <input
            type="text"
            className="form-control text-center fs-4 p-3 mt-3"
            placeholder={trn("app.consumer.setup.table_placeholder", "Table code")}
            value={tableCode}
            onChange={(e) => setTableCode(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={loading}
            style={{
              borderRadius: 18,
              border: "1px solid rgba(96, 165, 250, 0.35)",
              background: "rgba(15, 23, 42, 0.92)",
              color: "#fff",
            }}
          />
        ) : null}

        {err ? (
          <div className="d-flex align-items-center gap-2 text-warning mt-3">
            <AlertCircle size={18} /> {err}
          </div>
        ) : null}

        {loading ? (
          <div
            className="mt-4"
            style={{
              borderRadius: 18,
              border: "1px solid rgba(96, 165, 250, 0.25)",
              background: "rgba(15, 23, 42, 0.72)",
              padding: "16px 18px",
            }}
          >
            <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
              <div style={{ color: "#fff", fontWeight: 800 }}>
                {activationStep === "connecting"
                  ? trn("app.consumer.setup.progress_connecting", "Conectando con el servidor...")
                  : activationStep === "downloadingMenus"
                    ? trn("app.consumer.setup.progress_downloading_menus", "Descargando menús...")
                    : activationStep === "preparingImages"
                      ? imageProgress.total > 0
                        ? trn("app.consumer.setup.progress_preparing_images_count", "Preparando imágenes del menú {loaded}/{total}...")
                            .replace("{loaded}", String(imageProgress.loaded))
                            .replace("{total}", String(imageProgress.total))
                        : trn("app.consumer.setup.progress_preparing_images", "Preparando imágenes del menú...")
                      : trn("app.consumer.setup.progress_saving", "Guardando configuración...")}
              </div>
              <div style={{ color: "#93c5fd", fontWeight: 800, minWidth: 42, textAlign: "right" }}>
                {currentActivationProgress}%
              </div>
            </div>
            <div
              style={{
                height: 10,
                borderRadius: 999,
                background: "rgba(148, 163, 184, 0.22)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${currentActivationProgress}%`,
                  height: "100%",
                  borderRadius: 999,
                  background: `linear-gradient(90deg, ${platformMetadata.secondaryColor}, ${platformMetadata.primaryColor})`,
                  transition: "width 260ms ease",
                }}
              />
            </div>
            <p className="mt-2 mb-0" style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.45 }}>
              {trn(
                "app.consumer.setup.progress_hint",
                "Este proceso se hace durante la instalación para que el primer cliente no espere la descarga inicial."
              )}
            </p>
          </div>
        ) : null}

        <button
          className="btn btn-lg w-100 mt-4"
          onClick={() => void onActivate()}
          disabled={loading || !canActivate}
          style={{
            borderRadius: 18,
            border: "none",
            background: `linear-gradient(135deg, ${platformMetadata.secondaryColor}, ${platformMetadata.primaryColor})`,
            color: "#fff",
            fontWeight: 700,
            minHeight: 58,
          }}
        >
          {loading ? (
            <>
              <LoaderCircle className="me-2 spinner-border" size={22} />{" "}
              {activationStep === "downloadingMenus"
                ? trn("app.consumer.setup.downloading_menus_cta", "Descargando menús...")
                : activationStep === "preparingImages"
                  ? trn("app.consumer.setup.preparing_menu_cta", "Preparando menú...")
                  : trn("app.consumer.setup.initializing", "Initializing...")}
            </>
          ) : (
            <>
              <CheckCircle className="me-2" size={22} /> {trn("app.consumer.setup.load_cta", "Load kiosk")}
            </>
          )}
        </button>
          </>
        )}
      </div>
    </div>
  );
}
